import { Ban, Snowflake } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { findGradient } from "../constants/cardGradients";
import { COLOCA_WATERMARK_SVG_DATA_URL } from "../constants/cardWatermark";
import type { CardStylesConfig } from "../types/branding.types";

type CardPreviewVisualProps = {
  alias: string;
  last4: string;
  type: "VIRTUAL" | "PHYSICAL";
  frozen?: boolean;
  disabled?: boolean;
  brand?: string;
  exp?: string;
  holder?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  design?: CardStylesConfig | null;
  /** Renders the iframe-equivalent layout (watermark, no header row). */
  iframeLike?: boolean;
};

const sizeStyles: Record<NonNullable<CardPreviewVisualProps["size"]>, string> = {
  sm: "w-[220px]",
  md: "w-full max-w-[320px]",
  lg: "w-full max-w-[420px]",
};

const sizePadding: Record<NonNullable<CardPreviewVisualProps["size"]>, string> = {
  sm: "p-3",
  md: "p-4",
  lg: "p-5",
};

const sizeAliasText: Record<NonNullable<CardPreviewVisualProps["size"]>, string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
};

const sizePanText: Record<NonNullable<CardPreviewVisualProps["size"]>, string> = {
  sm: "text-xs",
  md: "text-base",
  lg: "text-lg",
};

// Keep in sync with CardVisual.module.css and CardRow.tsx BRAND_GRADIENT.
// Retint globally via --color-brand-* in src/index.css.
const BRAND_GRADIENT = [
  "radial-gradient(ellipse 80% 60% at 80% 0%, rgba(255,255,255,0.18), transparent 60%)",
  "radial-gradient(ellipse 100% 80% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)",
  "linear-gradient(135deg, var(--color-brand-700) 0%, var(--color-brand-900) 60%, var(--color-brand-950) 100%)",
].join(", ");

export const CardPreviewVisual = ({
  alias,
  last4,
  type,
  frozen = false,
  disabled = false,
  exp,
  holder,
  size = "md",
  className,
  design,
  iframeLike = false,
}: CardPreviewVisualProps) => {
  const { t } = useTranslations("cards");
  const pan = `•••• •••• •••• ${last4}`;

  const customGradient = design?.gradient_id
    ? [
        "radial-gradient(ellipse 80% 60% at 80% 0%, rgba(255,255,255,0.18), transparent 60%)",
        "radial-gradient(ellipse 100% 80% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)",
        findGradient(design.gradient_id).css,
      ].join(", ")
    : null;
  const secondaryLogoUrl = design?.secondary_logo_url ?? null;
  const secondaryLogoScale = design?.secondary_logo_scale ?? 1;
  // In iframe-like mode the secondary shares Coloca's 40px baseline so the
  // two corner marks stay on the same horizontal line.
  const secondaryLogoBase = iframeLike ? 40 : size === "lg" ? 36 : size === "sm" ? 22 : 28;
  const secondaryLogoPx = Math.round(secondaryLogoBase * secondaryLogoScale);
  // Anchor by centre so scaling the secondary stays aligned with Coloca's
  // centre instead of only extending downward.
  const secondaryLogoTranslateY = iframeLike ? `${(40 - secondaryLogoPx) / 2}px` : "0px";

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl text-white shadow-sm",
        "aspect-[1.586/1]",
        sizeStyles[size],
        className,
      )}
      style={{
        background: customGradient ?? BRAND_GRADIENT,
      }}
    >
      {iframeLike && !frozen && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-4 top-4 block select-none"
          style={{
            width: "40px",
            height: "40px",
            backgroundImage: `url("${COLOCA_WATERMARK_SVG_DATA_URL}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain",
          }}
        />
      )}

      {secondaryLogoUrl && !frozen && (
        <img
          src={secondaryLogoUrl}
          alt=""
          aria-hidden="true"
          className="absolute right-4 top-4 select-none object-contain"
          style={{
            width: `${secondaryLogoPx}px`,
            height: `${secondaryLogoPx}px`,
            transform: `translateY(${secondaryLogoTranslateY})`,
            pointerEvents: "none",
          }}
        />
      )}

      <div className={cn("relative grid h-full grid-rows-[auto_1fr_auto] gap-3", sizePadding[size])}>
        {iframeLike ? (
          <div aria-hidden="true" />
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/60">
                {type === "VIRTUAL" ? t("cards_type_virtual_short") : t("cards_type_fisica_short")}
              </div>
              <div className={cn("mt-0.5 truncate font-semibold text-white", sizeAliasText[size])}>
                {alias || "—"}
              </div>
            </div>
          </div>
        )}

        {size !== "sm" && (
          <div
            className={cn("self-end font-mono tracking-[0.15em] text-white", sizePanText[size])}
            style={{ textShadow: "0 1px rgba(0,0,0,0.4)" }}
          >
            {pan}
          </div>
        )}

        <div className="grid grid-cols-[1fr_auto_auto] items-end gap-x-3 font-mono text-[14px] font-medium text-white">
          {holder && size !== "sm" ? <div className="min-w-0 truncate font-sans">{holder}</div> : <div />}
          {exp && <div className="text-right">Exp: {exp}</div>}
          {size !== "sm" && <div className="text-right">CVV: ***</div>}
        </div>
      </div>

      {/* `disabled` wins over `frozen` because DISABLED is terminal. */}
      {disabled ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-destructive/20 backdrop-blur-[1px]">
          {/* DS has no --destructive-foreground; force text-white. */}
          <ColocaBadge
            variant="destructive"
            icon={Ban}
            className="!h-7 bg-destructive !px-3 !py-1 !text-sm text-white shadow-md [&_svg]:text-white"
          >
            {t("cards_status_disabled")}
          </ColocaBadge>
        </div>
      ) : frozen ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-sky-500/15 backdrop-blur-[1px]">
          {/* DS has no info token; use sky-600 as the stand-in. */}
          <ColocaBadge
            variant="secondary"
            icon={Snowflake}
            className="!h-7 bg-sky-600 !px-3 !py-1 !text-sm text-white shadow-md [&_svg]:text-white"
          >
            {t("cards_status_frozen")}
          </ColocaBadge>
        </div>
      ) : null}
    </div>
  );
};
